import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingDown, TrendingUp, Minus, Users, Building2, ChevronRight, Sparkles } from 'lucide-react';
import { Insight, InsightDetailModal } from './InsightDetailModal';
const MOCK_INSIGHTS: Insight[] = [{
  id: 'i1',
  title: 'PTP engagement drops 30% on Mondays across all sites',
  description: 'Analysis shows a significant drop in Pre-Task Plan signatures on Monday mornings, correlating with a 2.3x higher incident rate during those shifts.',
  impactScore: -18,
  effortLevel: 'Low',
  actionValue: 'Top Priority',
  affectedSites: 4,
  affectedWorkers: 47,
  trend: 'worsening',
  category: 'ptp',
  suggestedAction: 'Implement a mandatory 5-minute Monday morning safety stand-down before tool pickup.',
  supportingData: ['Average PTP completion time is 2 mins on Mondays vs 6 mins other days', '40% of Monday crews were not on site the previous Friday', 'Monday incidents cost an estimated $18K/month in indirect costs']
}, {
  id: 'i2',
  title: 'SteelCo Inc has 3x more new-worker risk flags than average',
  description: 'Contractor SteelCo Inc consistently assigns workers with <30 days experience to high-risk tasks without documented mentorship.',
  impactScore: -14,
  effortLevel: 'Medium',
  actionValue: 'Strategic',
  affectedSites: 2,
  affectedWorkers: 12,
  trend: 'stable',
  category: 'contractor',
  suggestedAction: 'Require SteelCo to submit a formal new-worker mentorship plan before next shift.',
  supportingData: ['12 new workers flagged in critical risk areas this month', 'No documented buddy system in place for these crews', 'Accounts for 45% of all new-worker risk flags company-wide']
}, {
  id: 'i3',
  title: 'Inspection scores decline after 3pm across all projects',
  description: 'End-of-day fatigue is leading to lower housekeeping and safety compliance scores in the final two hours of shifts.',
  impactScore: -11,
  effortLevel: 'Low',
  actionValue: 'Quick Win',
  affectedSites: 5,
  affectedWorkers: 120,
  trend: 'worsening',
  category: 'inspection',
  suggestedAction: 'Schedule 10-minute afternoon check-ins and shift heavy hazard tasks to morning hours.',
  supportingData: ['Housekeeping violations spike by 60% after 3:00 PM', 'Near-miss reports are 40% more likely in the last hour of shift', 'Pattern observed consistently across all 5 active projects']
}, {
  id: 'i4',
  title: 'Weather-related risks recurring weekly without standing protocol',
  description: 'High winds and rain are causing repeated work stoppages and risk flags, but sites lack a unified weather contingency plan.',
  impactScore: -8,
  effortLevel: 'High',
  actionValue: 'Strategic',
  affectedSites: 3,
  affectedWorkers: 85,
  trend: 'stable',
  category: 'weather',
  suggestedAction: 'Develop and distribute a company-wide weather threshold and contingency protocol.',
  supportingData: ['3 sites flagged for wind/rain risks this week alone', 'PTPs rarely include specific weather stop-work criteria', 'Estimated 15 hours of lost productivity per site due to reactive planning']
}, {
  id: 'i5',
  title: 'Court Square Floor 3 resolved 4 risks in 48 hours',
  description: 'The team at Court Square LIC Floor 3 has demonstrated exceptional speed in closing out critical and high risk flags.',
  impactScore: 5,
  effortLevel: 'Low',
  actionValue: 'Quick Win',
  affectedSites: 1,
  affectedWorkers: 24,
  trend: 'improving',
  category: 'positive',
  suggestedAction: 'Recognize the team and document their rapid-response process to share with other sites.',
  supportingData: ['Average time to resolution: 12 hours (company average: 36 hours)', '100% PTP engagement maintained during risk resolution', 'Proactive communication logged for every corrective action']
}];
export function InsightsTab() {
  const [sortBy, setSortBy] = useState<'impact' | 'quickWins'>('impact');
  const [selectedInsight, setSelectedInsight] = useState<Insight | null>(null);
  const sortedInsights = [...MOCK_INSIGHTS].sort((a, b) => {
    if (sortBy === 'impact') {
      // Sort by absolute impact score (highest first)
      return Math.abs(b.impactScore) - Math.abs(a.impactScore);
    } else {
      // Sort by Quick Wins first, then Top Priority
      const order = {
        'Quick Win': 0,
        'Top Priority': 1,
        Strategic: 2,
        Deprioritize: 3
      };
      return order[a.actionValue] - order[b.actionValue];
    }
  });
  const getTrendIcon = (trend: Insight['trend']) => {
    switch (trend) {
      case 'worsening':
        return <TrendingUp size={16} className="text-red-500" data-id="element-709" data-mp="183airmgj97b9" />;
      case 'improving':
        return <TrendingDown size={16} className="text-emerald-500" data-id="element-710" data-mp="185n8ilsj8lyv" />;
      case 'stable':
        return <Minus size={16} className="text-gray-500" data-id="element-711" data-mp="y1ghhcod35z6" />;
    }
  };
  const getActionValueColor = (value: Insight['actionValue']) => {
    switch (value) {
      case 'Top Priority':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'Quick Win':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Strategic':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'Deprioritize':
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };
  return <div className="flex flex-col h-full" data-id="element-712" data-mp="j83c9od76x5x">
      {/* Header & Controls */}
      <div className="flex items-center justify-between mb-4" data-id="element-713" data-mp="5cj0rdzsth8j">
        <p className="text-sm text-gray-600" data-id="element-714" data-mp="a8zt6ke16hrf">
          Strategic patterns identified across all projects
        </p>
        <div className="flex bg-gray-100 p-1 rounded-lg" data-id="element-715" data-mp="1zq0q96jrko67">
          <button onClick={() => setSortBy('impact')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${sortBy === 'impact' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-716" data-mp="24nivomykol9g">
            Highest Impact
          </button>
          <button onClick={() => setSortBy('quickWins')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${sortBy === 'quickWins' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-717" data-mp="1t7xpx63pdhht">
            Quick Wins
          </button>
        </div>
      </div>

      {/* Insights List */}
      <div className="flex-1 overflow-y-auto -mx-6 px-6 space-y-3 pb-4" data-id="element-718" data-mp="2h0e5n9zywxdy">
        {sortedInsights.map((insight, index) => <motion.button key={insight.id} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: index * 0.05
      }} onClick={() => setSelectedInsight(insight)} className="w-full text-left bg-white border border-gray-200 rounded-xl p-4 hover:border-indigo-300 hover:shadow-md transition-all group relative overflow-hidden" data-id="element-719" data-mp="278qk1pq3eake" data-mpi={index}>
            {/* Decorative left border for positive/negative */}
            <div className={`absolute left-0 top-0 bottom-0 w-1 ${insight.impactScore > 0 ? 'bg-emerald-500' : 'bg-amber-400'}`} data-id="element-720" data-mp="3m5ulyjs930ij" />

            <div className="flex items-start gap-4 pl-2" data-id="element-721" data-mp="3q4twcjwr3phd">
              {/* Impact Score Box */}
              <div className={`flex-shrink-0 w-16 h-16 rounded-xl flex flex-col items-center justify-center border ${insight.impactScore > 0 ? 'bg-emerald-50 border-emerald-100' : 'bg-amber-50 border-amber-200'}`} data-id="element-722" data-mp="3v3wq387a69a1">
                <span className={`text-lg font-bold ${insight.impactScore > 0 ? 'text-emerald-600' : 'text-amber-700'}`} data-id="element-723" data-mp="dqav1rf1xu6n">
                  {insight.impactScore > 0 ? '+' : ''}
                  {Math.abs(insight.impactScore)}%
                </span>
                <span className={`text-[9px] font-semibold uppercase tracking-wider leading-tight text-center ${insight.impactScore > 0 ? 'text-emerald-600/70' : 'text-amber-600/70'}`} data-id="element-724" data-mp="z0zlxuh0y3e1">
                  {insight.impactScore > 0 ? 'Improved' : 'Can reduce'}
                </span>
              </div>

              <div className="flex-1 min-w-0" data-id="element-725" data-mp="kcbqrpb3bvom">
                <div className="flex items-center gap-2 mb-1.5" data-id="element-726" data-mp="fch5q2dzy0bh">
                  <span className={`px-2 py-0.5 text-[10px] font-bold rounded border ${getActionValueColor(insight.actionValue)}`} data-id="element-727" data-mp="feaa1b1508gb">
                    {insight.actionValue}
                  </span>
                  <span className="text-[10px] font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded" data-id="element-728" data-mp="1ksrntk6t1rv">
                    Effort: {insight.effortLevel}
                  </span>
                </div>

                <h3 className="text-sm font-bold text-gray-900 mb-1 leading-snug pr-6" data-id="element-729" data-mp="289kxqmuyz7xr">
                  {insight.title}
                </h3>

                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500" data-id="element-730" data-mp="2d1m986kbcumq">
                  <div className="flex items-center gap-1" data-id="element-731" data-mp="1z9xxw5hw2a6t">
                    <Building2 size={12} data-id="element-732" data-mp="239ymuk2m63s4" />
                    <span data-id="element-733" data-mp="23b1e4t3um88k">{insight.affectedSites} Sites</span>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-734" data-mp="2r0vo92ws9ip4">
                    <Users size={12} data-id="element-735" data-mp="2vyx5rjek99yh" />
                    <span data-id="element-736" data-mp="2vxuj5no9ttod">{insight.affectedWorkers} Workers</span>
                  </div>
                  <div className="flex items-center gap-1 ml-auto" data-id="element-737" data-mp="2hzxh4br1sv2b">
                    {getTrendIcon(insight.trend)}
                    <span className="capitalize" data-id="element-738" data-mp="2mww9opv2ctye">{insight.trend}</span>
                  </div>
                </div>
              </div>

              <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-739" data-mp="16mbvh0kedblj">
                <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-full" data-id="element-740" data-mp="xbzgbheby54g">
                  <ChevronRight size={16} data-id="element-741" data-mp="11dbbs3jvxkkk" />
                </div>
              </div>
            </div>
          </motion.button>)}
      </div>

      {/* Detail Modal */}
      {selectedInsight && <InsightDetailModal insight={selectedInsight} onClose={() => setSelectedInsight(null)} data-id="element-742" data-mp="sl0y2c9s6wyk" />}
    </div>;
}